import { Button } from "@svea/design-system-components-react";
import {
	ButtonGroup,
	Dialog,
	FormWithTracking,
	formatSsn,
	Grid,
	isRequired,
	Stack,
	Text,
	TextField,
	useText,
	validateEmail,
} from "@sveabank/cc-common";
import Paper from "../../../components/paper";
import type { AssignEmailFormValues, Signer } from "../../../types/signing";
import { addApplicantEmailToSigners } from "../../../utils/sign";

type Props = {
	signers: Signer[];
	handleSend: (newSigners: AssignEmailFormValues) => Promise<void>;
	isOpen: boolean;
	setOpen: (isOpen: boolean) => void;
	applicantId: string;
	applicantEmail: string | null;
};

const AddEmailModal = ({
	signers,
	handleSend,
	isOpen,
	setOpen,
	applicantId,
	applicantEmail,
}: Props) => {
	const [t, texts] = useText();

	const alteredSigners = addApplicantEmailToSigners(signers, applicantId, applicantEmail);

	return (
		<Dialog
			isOpen={isOpen}
			setOpen={setOpen}
			size="medium"
			heading={t(texts.signPage.assignEmailModal.titlePackage)}
		>
			<FormWithTracking
				initialValues={{ alteredSigners }}
				onSubmit={handleSend}
				matomoInstance={window.Matomo}
				render={({ handleSubmit, submitting }) => (
					<form onSubmit={handleSubmit} name="signatorEmailForm" className="w-full">
						<Stack gap={4}>
							<Dialog.Content>
								<Stack gap={4}>
									{alteredSigners.map((signer, index) => (
										<Paper key={signer.nationalId}>
											<Grid>
												<Grid.Col
													span={{
														base: 12,
														tablet: 3,
													}}
												>
													<Stack>
														<Text bold>{`${signer.firstName} ${signer.lastName}`}</Text>
														<Text>{formatSsn(signer.nationalId)}</Text>
													</Stack>
												</Grid.Col>
												<Grid.Col
													span={{
														base: 12,
														tablet: 9,
													}}
												>
													<TextField
														disabled={signer.nationalId === applicantId}
														label={t(texts.common.email)}
														type="email"
														name={`alteredSigners[${index}].email`}
														placeHolder={t(texts.signPage.assignEmailModal.emailPlaceHolder)}
														fullWidth
														validators={[validateEmail, isRequired]}
													/>
												</Grid.Col>
											</Grid>
										</Paper>
									))}
								</Stack>
							</Dialog.Content>
							<Dialog.Footer>
								<ButtonGroup row>
									<Button variant="primary" type="submit" isPending={submitting}>
										{t(texts.signPage.assignEmailModal.sendButton)}
									</Button>
									<Button variant="secondary" type="button" onPress={() => setOpen(false)}>
										{t(texts.signPage.assignEmailModal.cancelButton)}
									</Button>
								</ButtonGroup>
							</Dialog.Footer>
						</Stack>
					</form>
				)}
			/>
		</Dialog>
	);
};

export default AddEmailModal;
